import axios from "axios";
import { getAuth } from "firebase/auth";
import { getAppCheckHeaders } from "../firebase/appCheck";
import { isMfaEnrollmentRole, isMfaRequiredApiError, notifyMfaRequired, redirectToMfaEnrollment } from "../layouts/v2/utils/mfa";

const baseURL = process.env.REACT_APP_API_URL;

// Authorization + X-Firebase-AppCheck (control 3 del plan de seguridad, modo observación).
// Si no hay token de App Check, la petición sale igual sin esa cabecera.
async function authHeaders(forceRefresh = false) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return {};
  const [token, appCheckHeaders] = await Promise.all([
    user.getIdToken(forceRefresh),
    Promise.resolve().then(() => getAppCheckHeaders()).catch(() => ({})),
  ]);
  return { Authorization: `Bearer ${token}`, ...appCheckHeaders };
}

// La pantalla de enrolamiento solo existe en la bifurcación SA de PrivateRouter. El rol sale
// del claim `rol` del token en caché (getIdTokenResult(false), sin forzar la renovación), el
// mismo que evalúa auth.middleware. Sin sesión o si no se puede leer el token, no se redirige.
async function callerCanEnrollMfa() {
  try {
    const user = getAuth().currentUser;
    if (!user) return false;
    const { claims } = await user.getIdTokenResult(false);
    return isMfaEnrollmentRole(claims?.rol);
  } catch (e) {
    return false;
  }
}

// Con responseType "blob" (apiGetBlob) axios entrega también el cuerpo de error como Blob, así
// que `err.response.data.code` no existe y un 403 MFA_REQUIRED de una descarga no llevaba al SA
// al enrolamiento. Se lee una copia del texto: leer un Blob no lo consume, el llamador puede
// volver a leerlo (readApiErrorMessage, readBlobError) y `err.response.data` no cambia.
const MAX_BLOB_ERROR_BYTES = 64 * 1024;

function blobText(blob) {
  if (typeof blob.text === "function") return blob.text();
  // jsdom (tests) no implementa Blob.text().
  if (typeof FileReader === "undefined") return Promise.resolve("");
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => resolve("");
    reader.readAsText(blob);
  });
}

async function readBlobErrorJson(data) {
  if (typeof Blob === "undefined" || !(data instanceof Blob)) return null;
  if (data.size > MAX_BLOB_ERROR_BYTES) return null;
  try {
    return JSON.parse(await blobText(data));
  } catch (e) {
    return null;
  }
}

async function isMfaRequiredResponse(err) {
  if (isMfaRequiredApiError(err)) return true;
  if (err?.response?.status !== 403) return false;
  const body = await readBlobErrorJson(err.response.data);
  return Boolean(body) && isMfaRequiredApiError({ response: { status: 403, data: body } });
}

// 403 {code: "MFA_REQUIRED"} (control 2b del plan de seguridad): el rol exige doble factor y
// el token no lo trae. Renovar el token no lo arregla, así que no se reintenta. A un SA se lo
// lleva a /seguridad/doble-factor con el aviso; a cualquier otro rol (backend mal configurado)
// no, porque para él la ruta no existe. En los dos casos se propaga el error al llamador.
// Con el portal cargado (sesión 5 del feature-log), AuthContext escucha el aviso y pasa la puerta
// del doble factor a "required" sin recargar: PrivateRouter deja solo la pantalla de doble factor
// y reemplaza la URL. Si nadie escucha, se recarga como antes. Sin bucles: el oyente solo fija un
// estado y redirectToMfaEnrollment no recarga si ya está en la pantalla.
async function rethrowWithMfaRedirect(err) {
  if ((await isMfaRequiredResponse(err)) && (await callerCanEnrollMfa())) {
    if (!notifyMfaRequired()) redirectToMfaEnrollment();
  }
  throw err;
}

async function requestWithRetry(makeRequest) {
  try {
    return await makeRequest(false);
  } catch (err) {
    if (await isMfaRequiredResponse(err)) return rethrowWithMfaRedirect(err);
    const status = err?.response?.status;
    if ((status === 401 || status === 403) && getAuth().currentUser) {
      try {
        return await makeRequest(true);
      } catch (retryErr) {
        return rethrowWithMfaRedirect(retryErr);
      }
    }
    throw err;
  }
}

export async function apiGet(path) {
  return requestWithRetry(async (forceRefresh) =>
    axios.get(`${baseURL}${path}`, { headers: await authHeaders(forceRefresh) })
  );
}

export async function apiPost(path, body) {
  return requestWithRetry(async (forceRefresh) =>
    axios.post(`${baseURL}${path}`, body, { headers: await authHeaders(forceRefresh) })
  );
}

export async function apiPut(path, body) {
  return requestWithRetry(async (forceRefresh) =>
    axios.put(`${baseURL}${path}`, body, { headers: await authHeaders(forceRefresh) })
  );
}

export async function apiPatch(path, body) {
  return requestWithRetry(async (forceRefresh) =>
    axios.patch(`${baseURL}${path}`, body, { headers: await authHeaders(forceRefresh) })
  );
}

export async function apiDelete(path) {
  return requestWithRetry(async (forceRefresh) =>
    axios.delete(`${baseURL}${path}`, { headers: await authHeaders(forceRefresh) })
  );
}

export async function apiGetBlob(path) {
  return requestWithRetry(async (forceRefresh) =>
    axios.get(`${baseURL}${path}`, {
      headers: await authHeaders(forceRefresh),
      responseType: "blob",
    })
  );
}

/**
 * Sube un archivo a Storage usando el patrón signed URL.
 * 1) Pide signed URL al backend (incluye autorización fina por `kind`).
 * 2) Hace PUT del archivo directo a Storage con los headers que el backend
 *    devuelve (incluyen el token de descarga para construir la URL pública).
 * 3) Devuelve la URL pública final que debes guardar en el doc del recurso.
 */
// S2-143: PUT con XHR para poder informar el progreso de subida (fetch no lo
// da). `signal` aborta la subida y rechaza con AbortError.
function putWithProgress(url, headers, body, { onProgress, signal }) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    xhr.open('PUT', url);
    Object.entries(headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      signal?.removeEventListener('abort', onAbort);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload falló: HTTP ${xhr.status}`));
    };
    xhr.onerror = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(new Error('Upload falló: error de red'));
    };
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal?.addEventListener('abort', onAbort, { once: true });
    xhr.send(body);
  });
}

// `onProgress(0..1)` y `signal` son opcionales (S2-143). Sin `onProgress` el
// PUT sigue yendo por fetch, como antes.
export async function apiUploadFile({ kind, file, context = {}, onProgress, signal }) {
  const { data } = await apiPost('/storageV2/sign-upload', {
    kind,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    context,
  });
  // S2-142: los kinds privados (adjuntos de tickets) devuelven `objectPath`
  // (ruta del objeto, sin URL pública); el resto sigue devolviendo `finalUrl`.
  const result = data?.objectPath || data?.finalUrl;
  if (!data?.uploadUrl || !result) {
    throw new Error('Backend no devolvió signed URL');
  }
  const headers = data.uploadHeaders || { 'Content-Type': file.type || 'application/octet-stream' };
  if (onProgress) {
    await putWithProgress(data.uploadUrl, headers, file, { onProgress, signal });
    return result;
  }
  const putRes = await fetch(data.uploadUrl, {
    method: 'PUT',
    headers,
    body: file,
    ...(signal ? { signal } : {}),
  });
  if (!putRes.ok) {
    throw new Error(`Upload falló: HTTP ${putRes.status}`);
  }
  return result;
}

// --- Public Quiz API ---
const PUBLIC_QUIZ_BASE = "/PublicQuiz";

export const bootstrapPublicQuiz = async ({ email, recaptchaToken }) => {
  const { data } = await apiPost(`${PUBLIC_QUIZ_BASE}/bootstrap`, {
    email,
    recaptchaToken,
  });
  return data;
};

export const savePublicQuizProfile = async ({
  email,
  ider,
  data,
  current_step = "profile",
  recaptchaToken,
}) => {
  const { data: response } = await apiPost(`${PUBLIC_QUIZ_BASE}/profile`, {
    email,
    ider,
    data,
    current_step,
    recaptchaToken,
  });
  return response;
};

export const savePublicQuizProgress = async ({
  email,
  ider,
  respuestas,
  files,
  current_step,
  // S2-129 — Pregunta abierta del diagnóstico. Solo viaja el TEXTO: el servidor
  // reconstruye question_id/question_text desde el quiz.
  open_answer,
  recaptchaToken,
}) => {
  const body = { email, ider, recaptchaToken };

  if (respuestas !== undefined) body.respuestas = respuestas;
  if (files !== undefined) body.files = files;
  if (current_step !== undefined) body.current_step = current_step;
  if (open_answer !== undefined) body.open_answer = open_answer;

  const { data } = await apiPost(`${PUBLIC_QUIZ_BASE}/progress`, body);
  return data;
};

export const submitPublicQuiz = async ({
  email,
  ider,
  respuestas,
  files,
  open_answer,
  recaptchaToken,
}) => {
  const body = {
    email,
    ider,
    respuestas,
    files,
    recaptchaToken,
  };

  // Aditivo: si no hay pregunta abierta el body queda exactamente como antes.
  if (open_answer !== undefined) body.open_answer = open_answer;

  const { data } = await apiPost(`${PUBLIC_QUIZ_BASE}/submit`, body);
  return data;
};

export const submitPublicQuizSupportForm = async ({
  email,
  name,
  lastname,
  company,
  phone,
  message,
  origin = "input_manual",
  recaptchaToken,
}) => {
  const { data } = await apiPost(`${PUBLIC_QUIZ_BASE}/support-form`, {
    email,
    name,
    lastname,
    company,
    phone,
    message,
    origin,
    recaptchaToken,
  });
  return data;
};

export const evaluateQuizImages = async ({ email, ider }) => {
  const { data } = await apiPost(`${PUBLIC_QUIZ_BASE}/evaluate-images`, {
    email,
    ider,
  });
  return data;
};